// Build-time replaced by Bun's define plugin; in dev (HMR) this is undefined
declare const __API_BASE_URL__: string | undefined;
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : "";

// Skew applied when deciding whether a cached token is still usable, so a request doesn't start
// with a token that's about to expire mid-flight.
const REFRESH_SKEW_MS = 60_000;

interface BookingSessionCache {
  token: string;
  expiresAt: number;
}

let cached: BookingSessionCache | null = null;

/**
 * Returns a booking-session token for the public AMS booking flow (see `publicApi.ts`), minting one
 * via POST /api/ams/booking-sessions on first use or once the cached token is near expiry. Uses a
 * plain `fetch` rather than `publicApi`'s RTK Query client to avoid a circular dependency, since
 * `publicApi` calls this function from its own `prepareHeaders`.
 */
export async function getBookingSessionToken(): Promise<string> {
  const now = Date.now();
  if (cached && cached.expiresAt - now > REFRESH_SKEW_MS) {
    return cached.token;
  }

  // Present the outgoing token, expired or not. The server carries its session id forward (see
  // BookingSessionsController), so a slot reservation held since ZIP entry stays owned by this
  // caller across the rollover; without it the caller silently becomes a new session and its held
  // slot is orphaned, surfacing much later as "Reservation not found or expired for this session."
  const response = await fetch(`${API_BASE_URL}/api/ams/booking-sessions`, {
    method: "POST",
    headers: cached ? { Authorization: `Bearer ${cached.token}` } : undefined,
  });
  if (!response.ok) {
    throw new Error("Failed to obtain a booking session.");
  }

  const body: { token: string; expiresAt: string } = await response.json();
  cached = { token: body.token, expiresAt: new Date(body.expiresAt).getTime() };
  return cached.token;
}

/**
 * Marks the cached token unusable, forcing the next `getBookingSessionToken` call to fetch a
 * replacement. The token itself is deliberately retained rather than discarded, so that call can
 * still present it for renewal and keep the session id — and therefore the held slot reservation —
 * intact. A token the server declines to renew simply yields a new session, as before.
 */
export function clearBookingSessionToken(): void {
  if (cached) {
    cached = { ...cached, expiresAt: 0 };
  }
}
